/* eslint-disable @next/next/no-img-element */
"use client";

import { Button } from "@/components/new/button";
import { Input } from "@/components/new/input";
import { isValidUrl } from "@/lib/utils";
import { useCustomEditor } from "@/providers/custom-editor-provider";
import { LinkIcon, Loader2Icon } from "lucide-react";
import { useState } from "react";
import { LinkCard } from "@/components/common/link-card";
import { TPostLink } from "@/types/post";

function EditorLinkUpload() {
	const [url, setUrl] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [metadata, setMetadata] = useState<TPostLink | null>(null);
	const { closeLinkUploader, addLinkCard } = useCustomEditor();

	const fetchMetadata = async (inputUrl: string) => {
		if (!inputUrl) return;

		try {
			setIsLoading(true);
			const response = await fetch(`/api/metadata?url=${encodeURIComponent(inputUrl)}`);

			if (!response.ok) {
				throw new Error("Failed to fetch metadata");
			}

			const { data } = await response.json();

			if (data) {
				setMetadata({ ...data, url: inputUrl });
			}
		} catch (error) {
			console.error("Error fetching metadata:", error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		setUrl(inputValue);

		if (isValidUrl(inputValue)) {
			fetchMetadata(inputValue);
		}
	};

	const handleAddClick = async () => {
		await addLinkCard(url);

		closeLinkUploader();

		setUrl("");
		setMetadata(null);
	};

	return (
		<div className="space-y-4 py-4">
			{isLoading && (
				<div className="flex items-center justify-center rounded-md border p-8">
					<Loader2Icon className="h-8 w-8 animate-spin" />
				</div>
			)}

			{!isLoading && metadata && <LinkCard link={metadata} size="compact" />}

			<div className="flex items-center justify-between gap-4">
				<div className="relative w-full">
					<Input
						value={url}
						onChange={handleInputChange}
						placeholder="Enter the Link"
						className="pr-12"
					/>
					<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
						<LinkIcon className="h-4 w-4 text-gray-400" />
					</div>
				</div>

				<Button onClick={handleAddClick} disabled={isLoading || !metadata}>
					Add
				</Button>
			</div>
			<span className="pl-2 text-xs text-gray-400">Enter full URLs, with http://, https://</span>
		</div>
	);
}

export default EditorLinkUpload;
